import { useState } from "react";
import { X, ClipboardPaste, Wand2, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";

// Urgency levels for a purchase request. Colours follow tactical convention:
// LOW = neutral gray, STANDARD = steel blue, HIGH = amber, CRITICAL = red.
export const URGENCY_META = {
    low:      { label: "LOW",      color: "#78716C", short: "LOW"  },
    standard: { label: "STANDARD", color: "#4A90E2", short: "STD"  },
    high:     { label: "HIGH",     color: "#D4A017", short: "HIGH" },
    critical: { label: "CRITICAL", color: "#DC2626", short: "CRIT" },
};
export const URGENCY_ORDER = ["low", "standard", "high", "critical"];

// Common carriers with slugs. `""` = auto-detect from tracking number format.
const CARRIER_OPTIONS = [
    { slug: "", label: "AUTO-DETECT" },
    { slug: "ups", label: "UPS" },
    { slug: "fedex", label: "FEDEX" },
    { slug: "usps", label: "USPS" },
    { slug: "dhl", label: "DHL" },
    { slug: "amzn_us", label: "AMAZON LOGISTICS" },
    { slug: "ontrac", label: "ONTRAC" },
    { slug: "lasership", label: "LASERSHIP" },
    { slug: "royal-mail", label: "ROYAL MAIL" },
    { slug: "canada-post", label: "CANADA POST" },
];

const SOURCE_OPTIONS = [
    { value: "amazon", label: "AMAZON" },
    { value: "manual", label: "MANUAL / OTHER" },
    { value: "distributor", label: "DISTRIBUTOR" },
    { value: "vendor", label: "VENDOR / SUPPLIER" },
];

export default function OrderModal({ open, order, onClose, onSave, onDelete }) {
    const { user } = useAuth();
    const [form, setForm] = useState(order || {});
    const [pasteMode, setPasteMode] = useState(false);
    const [pasteText, setPasteText] = useState("");
    const [parsing, setParsing] = useState(false);

    const isNew = !order?.id;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    if (!open) return null;

    // Re-init when opened with a new order
    if (order && form && order.id !== form.id) {
        setForm(order);
    }

    const handleParse = async () => {
        if (!pasteText.trim()) {
            toast.warning("Paste an Amazon email or order details first.");
            return;
        }
        setParsing(true);
        try {
            const { data } = await api.post("/logistics/parse", { text: pasteText });
            const parsed = {
                ...form,
                ...data,
                quantity: data.quantity || form.quantity || 1,
            };
            setForm(parsed);
            setPasteMode(false);
            setPasteText("");
            toast.success("Parsed. Verify the fields and save.");
        } catch (e) {
            toast.error("Could not parse. Fill fields manually.");
        } finally {
            setParsing(false);
        }
    };

    const handleSave = () => {
        if (!form.item?.trim()) {
            toast.warning("Item name is required.");
            return;
        }
        const urgency = URGENCY_ORDER.includes(form.urgency) ? form.urgency : "standard";
        // Requested-by handling:
        // - If the user typed something, use it verbatim.
        // - If the field was left blank AND this is a NEW order being created,
        //   fall back to the currently-logged-in user (they placed it).
        // - If the field was left blank on an EXISTING order (which happens when
        //   the order came in via inbound email and no one has filled it in),
        //   preserve null — do NOT silently attribute the order to whoever is
        //   currently viewing it.
        const typedRequestedBy = (form.requested_by || "").trim();
        const resolvedRequestedBy = typedRequestedBy
            ? typedRequestedBy
            : (isNew ? (user?.email || null) : null);
        onSave({
            item: form.item.trim(),
            quantity: parseInt(form.quantity || 1, 10) || 1,
            source: form.source || "manual",
            order_date: form.order_date || null,
            tracking_number: (form.tracking_number || "").trim() || null,
            carrier: form.carrier || null,
            expected_delivery: form.expected_delivery || null,
            notes: form.notes || "",
            url: form.url || null,
            urgency,
            requested_by: resolvedRequestedBy,
        });
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="relative w-[600px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        {isNew ? "ORDER REQUEST" : "EDIT ORDER"}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-virtus-dim hover:text-virtus-orange"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    {!pasteMode && (
                        <button
                            onClick={() => setPasteMode(true)}
                            className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-virtus-orange text-virtus-orange hover:bg-virtus-orange/10 font-mono text-[10px] tracking-[0.28em] uppercase"
                            data-testid={TID.orderPasteToggleBtn}
                        >
                            <ClipboardPaste size={13} /> PASTE AMAZON ORDER EMAIL → AUTO-FILL
                        </button>
                    )}

                    {pasteMode && (
                        <div className="border border-virtus-orange p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange">
                                    PASTE FROM AMAZON (OR ANY CARRIER EMAIL)
                                </div>
                                <button
                                    onClick={() => {
                                        setPasteMode(false);
                                        setPasteText("");
                                    }}
                                    className="text-virtus-dim hover:text-virtus-orange"
                                    aria-label="Close paste"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                            <textarea
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                                rows={6}
                                autoFocus
                                placeholder="Paste the full email text here… VIRTUS will extract item, quantity, order date, and tracking number."
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white text-sm outline-none font-mono"
                                data-testid={TID.orderPasteTextarea}
                            />
                            <button
                                onClick={handleParse}
                                disabled={parsing}
                                className="w-full flex items-center justify-center gap-2 py-2 bg-virtus-orange text-black font-mono text-[10px] tracking-[0.28em] uppercase hover:bg-white disabled:opacity-50"
                                data-testid={TID.orderPasteParseBtn}
                            >
                                <Wand2 size={12} /> {parsing ? "PARSING..." : "PARSE & FILL"}
                            </button>
                        </div>
                    )}

                    <Field label="Item / material to purchase">
                        <input
                            value={form.item || ""}
                            onChange={(e) => set("item", e.target.value)}
                            placeholder="e.g. Marine-grade lithium battery, 24V 100Ah"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            data-testid={TID.orderItemInput}
                            autoFocus
                        />
                    </Field>

                    {/* Urgency selector — 4 pill buttons, colour-coded. */}
                    <Field label="Urgency">
                        <div className="flex gap-2" data-testid="order-urgency-group">
                            {URGENCY_ORDER.map((key) => {
                                const meta = URGENCY_META[key];
                                const active = (form.urgency || "standard") === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => set("urgency", key)}
                                        className="flex-1 flex items-center justify-center gap-1.5 py-2 border font-mono text-[10px] tracking-[0.28em] uppercase transition-colors"
                                        style={{
                                            borderColor: active ? meta.color : "#282828",
                                            backgroundColor: active ? `${meta.color}22` : "transparent",
                                            color: active ? meta.color : "#5A5A5A",
                                        }}
                                        data-testid={`order-urgency-${key}`}
                                    >
                                        {key === "critical" && <Flame size={11} />}
                                        {meta.label}
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Requested by (optional)">
                        <input
                            value={
                                form.requested_by !== undefined && form.requested_by !== null
                                    ? form.requested_by
                                    : (isNew ? (user?.email ?? "") : "")
                            }
                            onChange={(e) => set("requested_by", e.target.value)}
                            placeholder={isNew ? "operator@virtusmaritime.com" : "Not attributed — type an operator email to attribute"}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-xs"
                            data-testid="order-requested-by-input"
                        />
                    </Field>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Quantity">
                            <input
                                type="number"
                                min="1"
                                value={form.quantity || 1}
                                onChange={(e) => set("quantity", e.target.value)}
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono"
                                data-testid={TID.orderQtyInput}
                            />
                        </Field>
                        <Field label="Source">
                            <select
                                value={form.source || "manual"}
                                onChange={(e) => set("source", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {SOURCE_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Order date">
                            <input
                                type="date"
                                value={form.order_date || ""}
                                onChange={(e) => set("order_date", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                        <Field label="Expected delivery">
                            <input
                                type="date"
                                value={form.expected_delivery || ""}
                                onChange={(e) => set("expected_delivery", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-[1fr_180px] gap-4">
                        <Field label="Tracking #">
                            <input
                                value={form.tracking_number || ""}
                                onChange={(e) => set("tracking_number", e.target.value)}
                                placeholder="1Z999AA10123456784"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none font-mono text-sm"
                                data-testid={TID.orderTrackingInput}
                            />
                        </Field>
                        <Field label="Carrier">
                            <select
                                value={form.carrier || ""}
                                onChange={(e) => set("carrier", e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                            >
                                {CARRIER_OPTIONS.map((c) => (
                                    <option key={c.slug} value={c.slug}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Vendor">
                            <input
                                value={form.vendor || ""}
                                onChange={(e) => set("vendor", e.target.value)}
                                placeholder="Amazon, McMaster-Carr, …"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm"
                            />
                        </Field>
                        <Field label="Price (USD)">
                            <input
                                type="number"
                                step="0.01"
                                value={form.price ?? ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    set("price", v === "" ? null : parseFloat(v));
                                }}
                                placeholder="0.00"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm"
                            />
                        </Field>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Vendor contact email">
                            <input
                                type="email"
                                value={form.vendor_contact_email || ""}
                                onChange={(e) => set("vendor_contact_email", e.target.value)}
                                placeholder="support@vendor.com"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm"
                            />
                        </Field>
                        <Field label="Vendor contact phone">
                            <input
                                value={form.vendor_contact_phone || ""}
                                onChange={(e) => set("vendor_contact_phone", e.target.value)}
                                placeholder="1-888-000-0000"
                                className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm"
                            />
                        </Field>
                    </div>

                    <Field label="Product URL (optional)">
                        <input
                            value={form.url || ""}
                            onChange={(e) => set("url", e.target.value)}
                            placeholder="https://..."
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none text-sm"
                        />
                    </Field>

                    <Field label="Notes">
                        <textarea
                            value={form.notes || ""}
                            onChange={(e) => set("notes", e.target.value)}
                            rows={2}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </Field>
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-t border-virtus-line">
                    <div>
                        {!isNew && (
                            <button
                                onClick={onDelete}
                                className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-stuck hover:text-white px-3 py-2 border border-virtus-stuck hover:bg-virtus-stuck"
                                data-testid={TID.orderDeleteBtn}
                            >
                                DELETE
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleSave}
                            className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange"
                            data-testid={TID.orderSaveBtn}
                        >
                            {isNew ? "CREATE" : "SAVE"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-2">
                {label}
            </div>
            {children}
        </div>
    );
}
